/**
 * HomeAlerts — banner avvisi imminenti in primo piano sulla homepage.
 *
 * iter v28 r388 — User M1298: «deve uscire un avviso sulla homepage,
 * tipo "domani è il compleanno di questo, domani è Natale, manda..."».
 *
 * Risolve il problema del popup in basso a destra che è poco visibile.
 * Qui gli avvisi sono cartelloni grandi in cima alla home con CTA chiara.
 *
 * Fonti aggregate dal backend `/contacts/bulk-greetings/home-alerts`:
 *  - festività religiose nei prossimi 2 giorni
 *  - compleanni dei contatti nei prossimi 2 giorni
 *
 * Dismiss via X (silenzia per quest'anno per le festività, per questa
 * occorrenza per i compleanni).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle, X, ArrowRight, Cake, Tree, Confetti } from "@phosphor-icons/react";
import { api } from "@/lib/api";

function whenLabel(daysUntil) {
  if (daysUntil === 0) return "Oggi";
  if (daysUntil === 1) return "Domani";
  if (daysUntil === 2) return "Dopodomani";
  return `Tra ${daysUntil} giorni`;
}

export default function HomeAlerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch_ = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts/bulk-greetings/home-alerts");
      setAlerts(data?.alerts || []);
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch_(); }, []);

  const dismiss = async (alert) => {
    setAlerts((arr) => arr.filter((a) => a.id !== alert.id));
    try {
      if (alert.kind === "holiday") {
        const year = parseInt(alert.target_date.slice(0, 4), 10);
        await api.post("/contacts/bulk-greetings/holiday-dismiss", {
          key: alert.occasion_key,
          year,
        });
      } else if (alert.kind === "birthday") {
        await api.post("/contacts/bulk-greetings/birthday-dismiss", {
          contact_id: alert.contact_id,
          target_date: alert.target_date,
        });
      } else if (alert.kind === "match_property") {
        await api.post("/contacts/bulk-greetings/match-property-dismiss", {
          property_id: alert.property_id,
        });
      }
      // agenda: solo dismiss locale (è ricorrente, riapparirà al refresh)
    } catch { /* silent */ }
  };

  if (loading || alerts.length === 0) return null;

  return (
    <div className="space-y-2.5 mb-6" data-testid="home-alerts">
      {alerts.map((alert) => {
        let accent = "#7C8B6E";
        let accentBg = "#F1F4ED";
        if (alert.kind === "birthday") { accent = "#C97A4A"; accentBg = "#FBF2EA"; }
        else if (alert.kind === "agenda") { accent = "#3B7BBF"; accentBg = "#E8F0F8"; }
        else if (alert.kind === "match_property") { accent = "#5C6B4E"; accentBg = "#EDF2E5"; }
        const kindLabel = (
          alert.kind === "holiday" ? "Festività"
          : alert.kind === "birthday" ? "Compleanno cliente"
          : alert.kind === "agenda" ? "Agenda"
          : alert.kind === "match_property" ? `Nuovo ${alert.listing_noun || "annuncio"}`
          : ""
        );
        return (
          <div
            key={alert.id}
            className="bg-white border rounded-2xl px-4 py-3.5 sm:px-5 sm:py-4 flex items-center gap-3 sm:gap-4 shadow-sm relative overflow-hidden"
            style={{ borderColor: `${accent}55` }}
            data-testid={`home-alert-${alert.kind}-${alert.id}`}
          >
            {/* Accent bar */}
            <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: accent }} />

            <div
              className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-full inline-flex items-center justify-center text-[26px] sm:text-[30px]"
              style={{ background: accentBg }}
              aria-hidden="true"
            >
              {alert.emoji}
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-0.5" style={{ color: accent }}>
                {whenLabel(alert.days_until)} · {kindLabel}
              </div>
              <div className="text-[14px] sm:text-[15.5px] font-semibold text-[#1C1C1A] leading-tight truncate">
                {alert.title}
              </div>
            </div>

            <div className="shrink-0 flex items-center gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={() => navigate(alert.cta_route)}
                className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-white text-[11.5px] sm:text-[12px] font-semibold transition-colors"
                style={{ background: accent }}
                onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(0.92)")}
                onMouseLeave={(e) => (e.currentTarget.style.filter = "")}
                data-testid={`home-alert-cta-${alert.id}`}
              >
                <Sparkle size={11} weight="fill" />
                <span className="hidden sm:inline">{alert.cta_label}</span>
                <span className="sm:hidden">{alert.kind === "match_property" ? "Notifica" : alert.kind === "agenda" ? "Apri" : "Manda"}</span>
                <ArrowRight size={10} weight="bold" />
              </button>
              <button
                type="button"
                onClick={() => dismiss(alert)}
                className="w-8 h-8 rounded-full hover:bg-[#1C1C1A]/5 inline-flex items-center justify-center text-[#9A968B] hover:text-[#1C1C1A]"
                aria-label="Chiudi avviso"
                data-testid={`home-alert-dismiss-${alert.id}`}
              >
                <X size={13} weight="bold" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
